// Libraries
import { Flex, Link, Text, Tooltip, useBreakpointValue } from '@chakra-ui/react';
import NextLink from 'next/link';
import { memo } from 'react';
// Components
import { ArrowIcon } from '@atoms/index';
import { AdaptableDialog } from '@organisms/index';
// Types
import { DataTableCellProps } from './DataTableCell.types';

/**
 * DataTableCell component represents a cell in a data table.
 *
 * @component
 * @param {DataTableCellProps} props - DataTableCell props
 * @param {string} props.label - The label to be displayed in the cell.
 * @param {string} props.description - The description to be displayed in the tooltip.
 * @param {boolean} [props.isTooltipEnable=false] - Indicates whether the tooltip is enabled or not.
 * @param {string} [props.href] -
 * @param {string} [props.alignItems='start'] - The alignment of the cell's content.
 * @returns {ReactElement} The rendered DataTableCell component.
 */
export const DataTableCell = memo(
  ({
    label,
    description,
    shortDescription,
    isTooltipEnable = false,
    href,
    enableArrow,
    alignItems = 'start',
    justifyContent = 'space-between',
    fontWeight = 'normal',
    extendedRowInfo,
    containerStyles = {},
    ...rest
  }: DataTableCellProps) => {
    const displayButton = useBreakpointValue({ base: enableArrow, md: false }, { ssr: false });

    return (
      <Flex justifyContent={justifyContent} {...containerStyles}>
        <Tooltip
          hasArrow
          padding={2}
          label={<Text noOfLines={1}>{description || label}</Text>}
          bg='white'
          placement='top'
          openDelay={100}
          closeDelay={200}
          gutter={12}
          isDisabled={!isTooltipEnable}
        >
          <Flex px={4} py={1} alignItems={alignItems} flexDirection='column' {...rest}>
            {!!href ? (
              <NextLink href={href} passHref legacyBehavior>
                <Link fontSize='s' px={1} ml={-1}>
                  {label}
                </Link>
              </NextLink>
            ) : (
              <Text fontSize='s' fontWeight={fontWeight} noOfLines={2}>
                {label}
              </Text>
            )}
            {description && (
              <Text fontSize='s' variant='faded' lineHeight={2} noOfLines={1}>
                {description}
              </Text>
            )}
            {shortDescription && (
              <Text fontSize='xs' variant='faded' lineHeight={2} noOfLines={1}>
                {shortDescription}
              </Text>
            )}
          </Flex>
        </Tooltip>
        {displayButton && (
          <AdaptableDialog buttonContent={<ArrowIcon _hover={{ color: 'base' }} />}>{extendedRowInfo}</AdaptableDialog>
        )}
      </Flex>
    );
  }
);
